import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useResizeObserver } from 'usehooks-ts';

import Skeleton from 'components/Skeleton';
import { ElementNames, isTabletOrMobileDevice } from 'constants/app';
import { BettingTypes } from 'constants/markets';
import { useCurrency } from 'hooks/useCurrency';
import { useFormatCurrency } from 'hooks/useFormatCurrency';
import { useMarketUnits } from 'hooks/useMarketUnits';
import useOddsType from 'hooks/useOddsType';
import { getIsMarketGraphsDarkThemeEnabled } from 'redux/modules/appConfigs/selectors';
import { setElementHeight } from 'redux/modules/appSettings';
import {
  getMarketDefinitionMarketUnit,
  getMarketDefinitionSelectionStatuses
} from 'redux/modules/marketDefinitions/selectors';
import {
  getGraphMarketBettingType,
  getGraphMarketCurrency,
  getGraphMarketLastTradedPrice,
  getGraphMarketMatchedAmount,
  getGraphMarketMaxPrice,
  getGraphMarketMinPrice,
  getGraphSelectionMatchedAmount,
  getIsMarketDataLoaded
} from 'redux/modules/marketGraph/selectors';
import { getOddsValue } from 'utils/marketGraph';

import commonStyles from '../MarketGraphCommon.module.scss';
import styles from './MarketGraphTotalMatchedBlock.module.scss';

export default function MarketGraphTotalMatchedBlock() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { marketId, selectionId, handicap = 0 } = useParams();

  const currencyCode = useSelector(getGraphMarketCurrency);
  const matchedAmount = useSelector(getGraphMarketMatchedAmount);
  const selectionMatchedAmount = useSelector(getGraphSelectionMatchedAmount);
  const lastTradedPrice = useSelector(getGraphMarketLastTradedPrice);
  const minOdds = useSelector(getGraphMarketMinPrice);
  const maxOdds = useSelector(getGraphMarketMaxPrice);
  const selectionStatuses = useSelector(getMarketDefinitionSelectionStatuses(marketId || ''));
  const marketUnit = useSelector(getMarketDefinitionMarketUnit(marketId || ''));
  const bettingType = useSelector(getGraphMarketBettingType);
  const isDarkTheme = useSelector(getIsMarketGraphsDarkThemeEnabled);
  const isMarketDataLoaded = useSelector(getIsMarketDataLoaded);

  const { isAmericanOdds } = useOddsType();
  const marketUnitTranslated = useMarketUnits(marketUnit || 'points');
  const isLineMarket = bettingType === BettingTypes.LINE;
  const isAmericanOddsEnabled = isAmericanOdds && !isLineMarket;
  const oddsRangeLabel = isLineMarket
    ? t(`marketGraph.labels.unitsRange`, { UNITS: marketUnitTranslated })
    : t(`marketGraph.labels.oddsRange`);
  const lastOddsMatchedLabel = isLineMarket
    ? t(`marketGraph.labels.lastUnitsMatched`, { UNITS: marketUnitTranslated.toLowerCase() })
    : t(`marketGraph.labels.lastOddsMatched`);

  const hasSelectionData = !!minOdds && !!maxOdds;
  const reductionFactor = useMemo(() => {
    const runner = selectionStatuses?.find(
      selectionStatus => +selectionStatus.id === +(selectionId || 0) && +(selectionStatus.hc || 0) === +(handicap || 0)
    );
    return runner?.adjustmentFactor || null;
  }, [selectionStatuses, selectionId, handicap]);

  const currency = useCurrency(currencyCode);

  const { noFormattedAmount: matchedAmountFormatted } = useFormatCurrency(matchedAmount || 0, currency.currencyCode, {
    isCheckIndian: true,
    noRounding: true
  });

  const { noFormattedAmount: selectionMatchedAmountFormatted } = useFormatCurrency(
    selectionMatchedAmount || 0,
    currency.currencyCode,
    {
      isCheckIndian: true,
      noRounding: true
    }
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const isLoading = !isMarketDataLoaded;

  const items = useMemo(() => {
    return [
      {
        id: 'total-matched-market',
        title: 'marketGraph.labels.totalMatchedMarket',
        value: +(matchedAmount || 0) > 0 || hasSelectionData ? matchedAmountFormatted : '–',
        visible: true,
        addTitleTranslate: true,
        addValueBoldClassName: false
      },
      {
        id: 'total-matched-selection',
        title: 'marketGraph.labels.totalMatchedSelection',
        value: hasSelectionData ? selectionMatchedAmountFormatted : '–',
        visible: true,
        addTitleTranslate: true,
        addValueBoldClassName: false
      },
      {
        id: 'odds-range',
        title: oddsRangeLabel,
        value: hasSelectionData ? (
          <>
            {t('marketGraph.labels.low')}:{' '}
            <span className={styles.container__text__bold}>
              {getOddsValue({ odds: minOdds, isAmericanOdds: isAmericanOddsEnabled })}
            </span>
            {' / '}
            {t('marketGraph.labels.high')}:{' '}
            <span className={styles.container__text__bold}>
              {getOddsValue({ odds: maxOdds, isAmericanOdds: isAmericanOddsEnabled })}
            </span>
          </>
        ) : (
          '–'
        ),
        visible: true,
        addTitleTranslate: false,
        addValueBoldClassName: false
      },
      {
        id: 'last-odds-matched',
        title: lastOddsMatchedLabel,
        value: lastTradedPrice ? getOddsValue({ odds: lastTradedPrice, isAmericanOdds: isAmericanOddsEnabled }) : '–',
        visible: true,
        addTitleTranslate: true,
        addValueBoldClassName: !!lastTradedPrice
      },
      {
        id: 'reduction-factor',
        title: 'marketGraph.labels.reductionFactor',
        value: reductionFactor ? `${reductionFactor}%` : '–',
        visible: typeof reductionFactor === 'number',
        addTitleTranslate: true,
        addValueBoldClassName: false
      }
    ].filter(({ visible }) => visible);
  }, [
    hasSelectionData,
    selectionMatchedAmountFormatted,
    matchedAmountFormatted,
    reductionFactor,
    lastOddsMatchedLabel,
    lastTradedPrice,
    maxOdds,
    minOdds,
    oddsRangeLabel,
    isAmericanOddsEnabled
  ]);

  useResizeObserver({
    ref: containerRef,
    box: 'border-box',
    onResize: ({ height = 0 }) => {
      dispatch(setElementHeight({ name: ElementNames.MARKET_GRAPH_TOTAL_MATCHED_BLOCK, height }));
    }
  });

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, {
        [styles.container__mobile]: isTabletOrMobileDevice,
        [styles.container__dark]: isDarkTheme
      })}
    >
      {items.map(({ id, title, addTitleTranslate, addValueBoldClassName, value }) => {
        return (
          <div
            key={id}
            className={classNames(styles.container__row, {
              [styles.container__row__dark]: isDarkTheme
            })}
          >
            <p
              className={classNames(styles.container__text, styles.container__text__left, {
                [styles.container__text__mobile]: isTabletOrMobileDevice,
                [styles.container__text__left__dark]: isDarkTheme
              })}
            >
              {addTitleTranslate ? t(title) : title}:
            </p>
            {isLoading ? (
              <Skeleton
                withBrandingClassName={false}
                className={classNames(styles.container__skeleton, commonStyles.skeleton, {
                  [commonStyles.skeleton__dark]: isDarkTheme
                })}
              />
            ) : (
              <p
                className={classNames(styles.container__text, styles.container__text__right, {
                  [styles.container__text__mobile]: isTabletOrMobileDevice,
                  [styles.container__text__right__dark]: isDarkTheme,
                  [styles.container__text__bold]: addValueBoldClassName
                })}
              >
                {value}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
